// utils/cropImage.js

/**
 * Creates a cropped image from source image and crop area
 * @param {string} imageSrc - Source of image
 * @param {Object} croppedAreaPixels - Pixel data for cropping
 * @param {string} [format="file"] - Output format: "file" or "url"
 * @returns {Promise<File|string>} - Returns a File object or URL string based on format
 */
export const getCroppedImg = async (imageSrc, croppedAreaPixels, format = "file") => {
  try {
    if (!imageSrc || !croppedAreaPixels) {
      throw new Error("Missing image source or crop area");
    }

    // Create image element
    const image = new Image();
    image.src = imageSrc;
    
    // Wait for image to load
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    // Create canvas with proper dimensions
    const canvas = document.createElement("canvas");
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    const ctx = canvas.getContext("2d");

    // Draw cropped image
    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    // Create a blob from the canvas
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          
          if (format === "url") {
            // Return URL for preview purposes
            const croppedImageUrl = URL.createObjectURL(blob);
            resolve(croppedImageUrl);
          } else {
            // Return File object for upload
            const fileName = `profile-${Date.now()}.jpeg`;
            const file = new File([blob], fileName, { 
              type: "image/jpeg", 
              lastModified: Date.now() 
            });
            resolve(file);
          }
        },
        "image/jpeg",
        0.95 // Quality
      );
    });
  } catch (error) {
    // console.error("Error in cropping image:", error);
    throw error;
  }
};

// Default export for backward compatibility
export default getCroppedImg;
